import { useEffect, useMemo, useState } from 'react'
import { useI18n } from '../../../../utils/i18n'
import { useStorySegments } from './useStorySegments'

const API_ORIGIN = import.meta.env.VITE_API_ORIGIN || ''
const joinOriginPath = (origin, path) => origin ? `${origin.replace(/\/$/, '')}${path}` : path
const API_BASE = joinOriginPath(API_ORIGIN, '/api/story_editor')
const API_TEMPLATES = joinOriginPath(API_ORIGIN, '/api/templates')
const STORAGE_KEY = 'chamii.story_editor.ui_state.v1'
const LOCAL_STORE_KEY = 'chamii.story_editor.local_stories.v1'

export function useStoryEditor({ embedded = false }) {
  const { tx } = useI18n()
  const [stories, setStories] = useState([])
  const [loadingList, setLoadingList] = useState(false)
  const [currentId, setCurrentId] = useState('')
  const [story, setStory] = useState(null)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')

  const [step, setStep] = useState(1) // 1 选音频 -> 2 切分 -> 3 校对保存
  const [selectedSegmentIndex, setSelectedSegmentIndex] = useState(0)

  const [showNewStoryModal, setShowNewStoryModal] = useState(false)
  const [showDeleteStoryModal, setShowDeleteStoryModal] = useState(false)
  const [showDeleteSegmentModal, setShowDeleteSegmentModal] = useState(null)
  const [showTemplateModal, setShowTemplateModal] = useState(false)
  const [templates, setTemplates] = useState([])
  const [loadingTemplates, setLoadingTemplates] = useState(false)
  const [selectedTemplateId, setSelectedTemplateId] = useState('')
  const [importStoryId, setImportStoryId] = useState('')
  const [importTitle, setImportTitle] = useState('')
  const [importingTemplate, setImportingTemplate] = useState(false)
  const [localMode, setLocalMode] = useState(false)

  // 恢复 UI 状态（选中的故事/步骤/选中的段落）
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (!raw) return
      const state = JSON.parse(raw)
      if (state?.currentId) setCurrentId(state.currentId)
      if (state?.step) setStep(state.step)
      if (state?.selectedSegmentIndex !== undefined) setSelectedSegmentIndex(state.selectedSegmentIndex)
    } catch {
      // ignore
    }
  }, [])

  // 持久化 UI 状态
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          currentId,
          step,
          selectedSegmentIndex,
          ts: Date.now(),
        })
      )
    } catch {
      // ignore
    }
  }, [currentId, step, selectedSegmentIndex])

  const showMessage = (text, type = 'info') => {
    setMessage(text ? `${type === 'error' ? '❌ ' : type === 'ok' ? '✅ ' : ''}${text}` : '')
    if (text) setTimeout(() => setMessage(''), 3000)
  }

  const hasLocalAssets = (payload) => {
    const isLocal = (u) =>
      typeof u === 'string' && (u.startsWith('idb:') || u.startsWith('blob:') || u.startsWith('data:'))
    if (isLocal(payload?.source_audio)) return true
    const segs = Array.isArray(payload?.segments) ? payload.segments : []
    for (const s of segs) {
      if (isLocal(s?.audio_url)) return true
      if (isLocal(s?.image)) return true
      const imgs = Array.isArray(s?.images) ? s.images : []
      if (imgs.some(isLocal)) return true
      const markers = Array.isArray(s?.markers) ? s.markers : []
      for (const m of markers) {
        if (isLocal(m?.image)) return true
        if (isLocal(m?.imageUrl)) return true
      }
    }
    return false
  }

  const safeStoryId = (raw) => {
    const base = (raw || '').trim()
    if (!base) return ''
    return base
      .replace(/\s+/g, '_')
      .replace(/[^0-9A-Za-z_]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 64)
  }

  const fetchTemplates = async () => {
    setLoadingTemplates(true)
    try {
      const resp = await fetch(API_TEMPLATES)
      const data = await resp.json()
      setTemplates(data.templates || [])
    } catch (e) {
      showMessage(tx({ zh: '获取模板失败: {err}', en: 'Failed to fetch templates: {err}' }, { err: String(e) }), 'error')
    }
    setLoadingTemplates(false)
  }

  const openTemplateModal = async () => {
    setShowTemplateModal(true)
    await fetchTemplates()
  }

  const startImportTemplate = (tpl) => {
    setSelectedTemplateId(tpl?.id || '')
    const idGuess = safeStoryId(tpl?.name || tpl?.id || '')
    setImportStoryId(idGuess ? `${idGuess}_demo` : '')
    setImportTitle(tpl?.name || '')
  }

  const doImportTemplate = async () => {
    if (!selectedTemplateId) return
    const sid = safeStoryId(importStoryId)
    if (!sid) {
      showMessage(tx({ zh: '请填写 story_id（字母数字下划线）', en: 'Please enter story_id (letters/numbers/underscore)' }), 'error')
      return
    }
    setImportingTemplate(true)
    try {
      const resp = await fetch(`${API_TEMPLATES}/${encodeURIComponent(selectedTemplateId)}/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ story_id: sid, title: importTitle || selectedTemplateId }),
      })
      const data = await resp.json()
      if (!resp.ok || data.error) {
        showMessage(tx({ zh: '导入失败: {err}', en: 'Import failed: {err}' }, { err: String(data.error || resp.status) }), 'error')
      } else {
        showMessage(tx({ zh: '导入成功', en: 'Imported' }), 'ok')
        setShowTemplateModal(false)
        setSelectedTemplateId('')
        setImportStoryId('')
        setImportTitle('')
        await fetchStories()
        await loadStory(data.id)
      }
    } catch (e) {
      showMessage(tx({ zh: '导入失败: {err}', en: 'Import failed: {err}' }, { err: String(e) }), 'error')
    }
    setImportingTemplate(false)
  }

  const fetchStories = async () => {
    setLoadingList(true)
    try {
      const resp = await fetch(API_BASE)
      if (!resp.ok) throw new Error(`API ${resp.status}`)
      const data = await resp.json()
      setStories(data.stories || [])
      setLocalMode(false)
    } catch (e) {
      // Pages 上通常没有后端：自动降级到本地 Demo/本地存储模式
      try {
        const raw = localStorage.getItem(LOCAL_STORE_KEY)
        if (raw) {
          const parsed = JSON.parse(raw)
          const list = Object.values(parsed || {}).map((s) => ({ id: s.id, title: s.title || s.id }))
          if (list.length) {
            setStories(list)
            setLocalMode(true)
            setLoadingList(false)
            return
          }
        }
      } catch {
        // ignore
      }
      try {
        const demoResp = await fetch('/demo/stories.json', { cache: 'no-store' })
        if (demoResp.ok) {
          const demo = await demoResp.json()
          setStories(demo.stories || [])
          setLocalMode(true)
          setLoadingList(false)
          return
        }
      } catch {
        // ignore
      }
      setLocalMode(true)
      showMessage(
        tx({
          zh: '后端不可用，已切换到本地模式（仅本机保存）。',
          en: 'Backend unavailable; switched to local-only mode (saved in this browser only).',
        }),
        'error'
      )
    }
    setLoadingList(false)
  }

  useEffect(() => {
    fetchStories()
  }, [])

  // 如果本地记住了 currentId，拉完列表后自动加载
  useEffect(() => {
    if (!currentId) return
    if (!stories?.length) return
    loadStory(currentId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stories?.length])

  const loadStory = async (id) => {
    setCurrentId(id)
    if (!id) {
      setStory(null)
      setStep(1)
      setSelectedSegmentIndex(0)
      return
    }

    const loadLocal = async () => {
      try {
        const raw = localStorage.getItem(LOCAL_STORE_KEY)
        if (raw) {
          const parsed = JSON.parse(raw) || {}
          if (parsed[id]) return parsed[id]
        }
      } catch {
        // ignore
      }
      try {
        const demoResp = await fetch(`/demo/${encodeURIComponent(id)}.json`, { cache: 'no-store' })
        if (demoResp.ok) return await demoResp.json()
      } catch {
        // ignore
      }
      return null
    }

    try {
      let data = null
      if (!localMode) {
        const resp = await fetch(`${API_BASE}/${encodeURIComponent(id)}`)
        if (resp.ok) data = await resp.json()
        else setLocalMode(true)
      }
      if (!data) data = await loadLocal()
      if (!data) {
        showMessage(tx({ zh: '加载故事失败: 找不到 {id}', en: 'Load failed: {id} not found' }, { id }), 'error')
        return
      }

      const segments = (data.segments || []).map((seg) => ({
        text: seg.text || '',
        image: seg.image || (seg.images && seg.images[0]) || '',
        extraImages: (seg.images || []).slice(1).join('\n'),
        audio_url: seg.audio_url || '',
        markers: Array.isArray(seg.markers) ? seg.markers : [],
        duration_ms: seg.duration_ms || 3000,
        frame_duration_ms: seg.frame_duration_ms || 500,
        start_time: seg.start_time,
        end_time: seg.end_time,
      }))
      setStory({
        id: data.id || id,
        title: data.title || id,
        source_audio: data.source_audio || '',
        segments,
      })
      setStep(1)
      setSelectedSegmentIndex(0)
      showMessage(
        localMode ? tx({ zh: '故事已加载（本地模式）', en: 'Story loaded (local mode)' }) : tx({ zh: '故事已加载', en: 'Story loaded' }),
        'ok'
      )
    } catch (e) {
      showMessage(tx({ zh: '加载故事失败: {err}', en: 'Load failed: {err}' }, { err: String(e) }), 'error')
    }
  }

  const createNewStory = (id) => {
    if (!id) return
    setCurrentId(id)
    setStory({ id, title: id, source_audio: '', segments: [] })
    setStep(1)
    setSelectedSegmentIndex(0)
    showMessage(tx({ zh: '已创建本地故事草稿', en: 'Local draft created' }))
  }

  const {
    updateSegment, addSegment, removeSegment, moveSegment,
    handleSegmentsFromSplitter, handleSegmentTimeChange, updateSegmentWindow,
    nudgeSegmentStart, nudgeSegmentEnd,
  } = useStorySegments({
    story, setStory, setStep, setSelectedSegmentIndex, showMessage, tx,
  })

  const ensureStepValid = (nextStep) => {
    if (!story) return 1
    if (nextStep <= 1) return 1
    if (nextStep === 2) return story.source_audio ? 2 : 1
    if (nextStep === 3) return story.source_audio ? 3 : 1
    return 1
  }

  useEffect(() => {
    if (!story) return
    setStep((prev) => ensureStepValid(prev))
  }, [story?.source_audio])

  useEffect(() => {
    if (!story) return
    const len = story.segments?.length || 0
    if (len === 0) setSelectedSegmentIndex(0)
    else if (selectedSegmentIndex >= len) setSelectedSegmentIndex(len - 1)
  }, [story?.segments?.length])

  const handleSave = async () => {
    if (!story || !story.id) {
      showMessage(tx({ zh: '缺少故事 ID', en: 'Missing story ID' }), 'error')
      return
    }
    if (!story.title) {
      showMessage(tx({ zh: '请填写故事标题', en: 'Please enter a story title' }), 'error')
      return
    }
    setSaving(true)
    try {
      const payload = {
        id: story.id,
        title: story.title,
        source_audio: story.source_audio || '',
        segments: (story.segments || []).map((s) => {
          const images = []
          if (s.image) images.push(s.image)
          if (s.extraImages) {
            s.extraImages
              .split(/\n+/)
              .map((x) => x.trim())
              .filter(Boolean)
              .forEach((u) => images.push(u))
          }
          const seg = {
            text: s.text || '',
            duration_ms: Number(s.duration_ms) || 3000,
            frame_duration_ms: Number(s.frame_duration_ms) || 500,
            start_time: s.start_time,
            end_time: s.end_time,
            markers: Array.isArray(s.markers) ? s.markers : [],
          }
          if (images.length === 1) seg.image = images[0]
          if (images.length > 1) seg.images = images
          if (s.audio_url) seg.audio_url = s.audio_url
          return seg
        }),
      }
      const forceLocal = localMode || hasLocalAssets(payload)
      if (forceLocal) {
        try {
          if (!localMode) {
            setLocalMode(true)
            showMessage(
              tx({
                zh: '检测到本地上传资源：本次将保存到本地模式（仅本机可用）。',
                en: 'Local uploaded assets detected: saving in local-only mode (this browser only).',
              }),
              'info',
            )
          }
          const raw = localStorage.getItem(LOCAL_STORE_KEY)
          const store = raw ? JSON.parse(raw) : {}
          store[payload.id] = payload
          localStorage.setItem(LOCAL_STORE_KEY, JSON.stringify(store))
          showMessage(tx({ zh: '保存成功（本地）', en: 'Saved (local)' }), 'ok')
          fetchStories()
        } catch (e) {
          showMessage(tx({ zh: '保存失败（本地）: {err}', en: 'Save failed (local): {err}' }, { err: e }), 'error')
        }
      } else {
        const resp = await fetch(`${API_BASE}/${encodeURIComponent(story.id)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        })
        const data = await resp.json().catch(() => ({}))
        if (!resp.ok || data.error) {
          showMessage(tx({ zh: '保存失败: {err}', en: 'Save failed: {err}' }, { err: data.error || resp.status }), 'error')
        } else {
          showMessage(tx({ zh: '保存成功', en: 'Saved' }), 'ok')
          fetchStories()
        }
      }
    } catch (e) {
      showMessage(tx({ zh: '保存失败: {err}', en: 'Save failed: {err}' }, { err: e }), 'error')
    }
    setSaving(false)
  }

  const handleDeleteStory = async () => {
    if (!currentId) return
    try {
      if (localMode) {
        const raw = localStorage.getItem(LOCAL_STORE_KEY)
        const store = raw ? JSON.parse(raw) : {}
        delete store[currentId]
        localStorage.setItem(LOCAL_STORE_KEY, JSON.stringify(store))
        showMessage(tx({ zh: '删除成功（本地）', en: 'Deleted (local)' }), 'ok')
        setStory(null)
        setCurrentId('')
        setStep(1)
        setSelectedSegmentIndex(0)
        fetchStories()
      } else {
        const resp = await fetch(`${API_BASE}/${encodeURIComponent(currentId)}`, { method: 'DELETE' })
        const data = await resp.json().catch(() => ({}))
        if (!resp.ok || data.error) {
          showMessage(tx({ zh: '删除失败: {err}', en: 'Delete failed: {err}' }, { err: String(data.error || resp.status) }), 'error')
        } else {
          showMessage(tx({ zh: '删除成功', en: 'Deleted' }), 'ok')
          setStory(null)
          setCurrentId('')
          setStep(1)
          setSelectedSegmentIndex(0)
          fetchStories()
        }
      }
    } catch (e) {
      showMessage(tx({ zh: '删除失败: {err}', en: 'Delete failed: {err}' }, { err: String(e) }), 'error')
    }
  }

  const stepMeta = useMemo(
    () => [
      { id: 1, title: tx({ zh: '第 1 步：源音频', en: 'Step 1: Source audio' }), desc: tx({ zh: '上传/裁剪后再切分', en: 'Upload/trim, then split' }) },
      { id: 2, title: tx({ zh: '第 2 步：切分', en: 'Step 2: Split' }), desc: tx({ zh: '点击波形打点', en: 'Click waveform to add split points' }) },
      { id: 3, title: tx({ zh: '第 3 步：校对', en: 'Step 3: Review' }), desc: tx({ zh: '逐段修改并保存', en: 'Edit segments and save' }) },
    ],
    [tx]
  )

  const canGoStep2 = !!story?.source_audio
  const canGoStep3 = !!story?.source_audio
  const activeSeg = story?.segments?.[selectedSegmentIndex]
  const demoStoryId = 'story_editor_demo'
  const demoStory = stories.find((s) => s.id === demoStoryId)


  return {
    activeSeg, addSegment, canGoStep2, canGoStep3, createNewStory, currentId,
    demoStory, demoStoryId, doImportTemplate, embedded, ensureStepValid,
    fetchStories, handleDeleteStory, handleSave, handleSegmentTimeChange,
    handleSegmentsFromSplitter, importStoryId, importTitle, importingTemplate,
    loadStory, loadingList, loadingTemplates, message, moveSegment,
    nudgeSegmentEnd, nudgeSegmentStart, openTemplateModal, removeSegment,
    safeStoryId, saving, selectedSegmentIndex, selectedTemplateId,
    setImportStoryId, setImportTitle, setSelectedSegmentIndex,
    setSelectedTemplateId, setShowDeleteSegmentModal, setShowDeleteStoryModal,
    setShowNewStoryModal, setShowTemplateModal, setStep, setStory,
    showDeleteSegmentModal, showDeleteStoryModal, showNewStoryModal,
    showTemplateModal, startImportTemplate, step, stepMeta, stories, story,
    templates, tx, updateSegment, updateSegmentWindow,
  }
}
